import type { User } from '../services/httpServices/userService';

/**
 * Resolves the correct edit route for a user account.
 *
 * Staff accounts are created from the Employees / Doctors screens and share the
 * same underlying record, so editing them from the Users list should open the
 * richer Employee/Doctor edit form (which includes the system-user section)
 * instead of the generic `/users/:id/edit` form.
 *
 * When `hasAccess` is provided and the caller cannot enter the staff section,
 * the generic user edit route is returned as a fallback so the action never
 * dead-ends on the dashboard redirect.
 */
export function getUserEditPath(
  user?: Pick<User, 'id' | 'employee'> | null,
  hasAccess?: (path: string) => boolean,
): string {
  if (!user) return '/users';

  const employee = user.employee;
  if (employee?.id) {
    const isDoctor = employee.type === 'doctor';
    const staffPath = isDoctor ? `/doctors/${employee.id}/edit` : `/employees/${employee.id}/edit`;
    if (!hasAccess || hasAccess(isDoctor ? '/doctors' : '/employees')) {
      return staffPath;
    }
  }

  return `/users/${user.id}/edit`;
}
